import { useState, useEffect } from "react";
import { apiClient, API_BASE_URL } from "../../lib/api";
import { WorksPlacementList } from "./WorksPlacementList";
import { IllustrationSelectorModal } from "./IllustrationSelectorModal";
import { WorkGroup, Illustration } from "../../lib/types";

export interface WorksPlacement {
  id: number;
  image_id: string;
  works_id: number;
  display_order: number;
  is_visible: boolean;
  additional_config: Record<string, unknown>;
  created_at: string;
  illustration: {
    id: string;
    title: string;
    storage_path: string;
  };
  work: {
    id: number;
    name: string;
    icon_path: string | null;
  };
}

export function WorksPlacementManager() {
  const [placements, setPlacements] = useState<WorksPlacement[]>([]);
  const [works, setWorks] = useState<WorkGroup[]>([]);
  const [illustrations, setIllustrations] = useState<Illustration[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedWorkId, setSelectedWorkId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPlacement, setNewPlacement] = useState({
    image_id: "",
    works_id: 0,
    display_order: 0,
    is_visible: true,
    additional_config: "{}",
  });

  // データの取得
  const fetchData = async () => {
    try {
      // 作品グループの取得
      const worksData = await apiClient.get("/works") as WorkGroup[];
      setWorks(worksData);

      // イラストの取得
      const illustrationsData = await apiClient.get("/illustrations") as Illustration[];
      // categoryがworksのものに限定
      setIllustrations(illustrationsData.filter(i => i.category === "works"));

      // 配置情報の取得
      const placementsData = await apiClient.get("/works_placements") as any[];
      setPlacements(placementsData);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // 作品グループ変更時のハンドラ
  const handleWorkChange = (worksId: number) => {
    setSelectedWorkId(worksId);
    if (!worksId) {
      setNewPlacement({
        ...newPlacement,
        works_id: 0,
        display_order: 0,
      });
      return;
    }

    // 現在の最大display_orderを取得
    const workPlacements = placements.filter((p) => p.works_id === worksId);
    const maxOrder =
      workPlacements.length > 0
        ? Math.max(...workPlacements.map((p) => p.display_order)) + 1
        : 0;

    setNewPlacement({
      ...newPlacement,
      works_id: worksId,
      display_order: maxOrder,
    });
  };

  // 新規配置の追加
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      await apiClient.post("/works_placements", {
        ...newPlacement,
        additional_config: JSON.parse(newPlacement.additional_config),
      });

      setSuccess("画像を配置しました");
      setTimeout(() => setSuccess(null), 3000);

      // フォームをリセット（works_idは現在の選択を維持）
      setNewPlacement({
        image_id: "",
        works_id: selectedWorkId || 0, // 選択中のworks_idを維持
        display_order: newPlacement.display_order + 1, // 次の表示順序を設定
        is_visible: true,
        additional_config: "{}",
      });

      // 一覧を更新
      fetchData();
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  // 配置の削除
  const handleDelete = async (id: string | number) => {
    if (!window.confirm("この配置を削除してもよろしいですか？")) {
      return;
    }

    try {
      await apiClient.delete(`/works_placements/${id}`);
      setSuccess("配置を削除しました");
      setTimeout(() => setSuccess(null), 3000);
      fetchData();
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="p-4">
      <div className="space-y-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            {success}
          </div>
        )}

        {/* 作品グループ選択 */}
        <div className="border rounded-md p-4">
          <label className="block text-sm font-medium text-gray-700 mb-4">
            作品グループを選択 <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-4">
            {works.map((work) => (
              <button
                key={work.id}
                onClick={() => handleWorkChange(work.id)}
                className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${selectedWorkId === work.id
                  ? "border-indigo-500 bg-indigo-50"
                  : "border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50"
                  }`}
              >
                {work.icon_path && (
                  <img
                    src={`${API_BASE_URL}/storage/illustrations/${work.icon_path}`}
                    alt={work.name}
                    className="w-16 h-16 object-contain mb-2"
                  />
                )}
                <span className="text-sm font-medium text-gray-900">
                  {work.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* 新規配置フォーム */}
        {selectedWorkId && (
          <form
            onSubmit={handleSubmit}
            className="border rounded-md p-4 space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  イラスト <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    イラストを選択
                  </button>
                  {newPlacement.image_id && (
                    <div className="flex items-center space-x-2 border rounded-md p-2">
                      <img
                        src={`${API_BASE_URL}/storage/illustrations/${illustrations.find(
                          (ill) => ill.id === newPlacement.image_id
                        )?.storage_path
                          }`}
                        alt="選択されたイラスト"
                        className="w-10 h-10 object-contain"
                      />
                      <span className="text-sm text-gray-500">
                        {
                          illustrations.find(
                            (ill) => ill.id === newPlacement.image_id
                          )?.title
                        }
                      </span>
                    </div>
                  )}
                </div>
                <IllustrationSelectorModal
                  isOpen={isModalOpen}
                  onClose={() => setIsModalOpen(false)}
                  illustrations={illustrations.filter(
                    (illustration) =>
                      !placements.find(
                        (p) =>
                          p.works_id === selectedWorkId &&
                          p.image_id === illustration.id
                      )
                  )}
                  onSelect={(illustration) => {
                    setNewPlacement({
                      ...newPlacement,
                      image_id: illustration.id,
                    });
                    setIsModalOpen(false);
                  }}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  表示順序 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={newPlacement.display_order}
                  onChange={(e) =>
                    setNewPlacement({
                      ...newPlacement,
                      display_order: parseInt(e.target.value),
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  表示/非表示
                </label>
                <input
                  type="checkbox"
                  checked={newPlacement.is_visible}
                  onChange={(e) =>
                    setNewPlacement({
                      ...newPlacement,
                      is_visible: e.target.checked,
                    })
                  }
                  className="mt-1 rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
            </div>

            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              作品を追加
            </button>
          </form>
        )}

        {/* 配置一覧 */}
        <WorksPlacementList
          works={works}
          placements={placements}
          onDelete={handleDelete}
          onUpdate={fetchData}
        />
      </div>
    </div>
  );
}
